import React from 'react';
import InfoTip from './InfoTip';
import { formatCount, formatPercent } from './format';

function OutcomeBar({ label, info, percent, part, whole, colour, caption }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <p className="flex items-center gap-1 text-sm text-gray-900">
          {label}
          <InfoTip text={info} />
        </p>
        <p className="text-2xl font-semibold tabular-nums text-gray-900">
          {formatPercent(percent)}
        </p>
      </div>
      <div className="mt-2 h-2.5 rounded-full bg-gray-100 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{
            width: `${Math.min(percent ?? 0, 100)}%`,
            backgroundColor: colour,
          }}
        />
      </div>
      <p className="mt-2 text-xs text-gray-500">
        {formatCount(part)} of {formatCount(whole)} {caption}
      </p>
    </div>
  );
}

// "Outcomes in ended cohorts": active and graduated, each measured against
// enrolment (not chained), from the level's funnel. Live cohorts are left
// out so they don't pull the rates down.
function OutcomesCard({ funnel, definitions, inProgress }) {
  const stages = funnel?.stages ?? [];
  const covered = funnel?.cohorts ?? 0;
  const byStage = Object.fromEntries(stages.map((s) => [s.stage, s]));
  const enrolled = byStage.enrolled?.count;

  return (
    <section className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex flex-wrap items-baseline justify-between gap-2 mb-5">
        <h2 className="text-base font-semibold text-gray-900">
          Outcomes in ended cohorts
        </h2>
        {covered > 0 && (
          <p className="text-xs text-gray-500">
            {covered} cohort{covered === 1 ? '' : 's'} · {formatCount(enrolled)}{' '}
            enrolled · live cohorts excluded
          </p>
        )}
      </div>

      {covered === 0 || stages.length === 0 ? (
        <p className="text-sm text-gray-500">
          {inProgress
            ? 'In progress. Outcomes appear once a cohort has ended.'
            : 'No progress data for these cohorts yet.'}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <OutcomeBar
              label="Active in the LMS"
              info={definitions.active}
              percent={byStage.active?.percent_of_enrolled}
              part={byStage.active?.count}
              whole={enrolled}
              colour="#00999E"
              caption="recorded a quiz, submission or completed week."
            />
            <OutcomeBar
              label="Graduated"
              info={definitions.graduated}
              percent={byStage.graduated?.percent_of_enrolled}
              part={byStage.graduated?.count}
              whole={enrolled}
              colour="#8B173B"
              caption="hold a certificate."
            />
          </div>
          <p className="mt-5 pt-4 border-t border-gray-100 text-xs text-gray-500">
            Both rates are measured against enrolment, not chained. Graduations
            before 2025 come from the older certification flag, often without
            LMS activity, so some graduates were never counted as active.
          </p>
        </>
      )}
    </section>
  );
}

export default OutcomesCard;
